// Which saved hashtags a composer prefills.
//
// The SAME RULES as the backend's autopilot-backend/hashtags.js
// (normaliseHashtagPool + pickHashtags), which picks the tags for generated
// posts. The dashboard cannot import that file (CRA only builds src/), so this
// is a port; keep the two in step.
//
// WHY: the carousel and video composers prefilled every saved tag, and saving
// the post kept only the FIRST five (server.js capHashtags) — so every carousel
// and video went out with the same five tags, however many were saved. Now the
// composers draw five at random from all of them, like generated posts do.

export const DEFAULT_HASHTAG_COUNT = 5;

// Flatten, trim, drop empties, prefix '#', and de-duplicate
// case-insensitively, keeping the first spelling the client typed.
// hashtag_sets has held both an array of strings and an array of arrays, so
// it is flattened first; anything that is not an array means no tags.
export function normaliseHashtagPool(hashtagSets) {
  const flat = Array.isArray(hashtagSets) ? hashtagSets.flat(Infinity) : [];
  const seen = new Set();
  const out = [];
  for (const raw of flat) {
    if (raw == null) continue;
    const trimmed = String(raw).trim();
    if (!trimmed) continue;
    const tag = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
}

// `count` tags at random, no repeats (partial Fisher-Yates over a copy).
// Fewer saved than `count` → all of them; none saved → []. `rng` behaves like
// Math.random and is injectable so tests can pin what "random" did.
export function pickHashtags(hashtagSets, { count = DEFAULT_HASHTAG_COUNT, rng = Math.random } = {}) {
  const pool = normaliseHashtagPool(hashtagSets);
  const take = Math.min(count, pool.length);
  if (take <= 0) return [];
  for (let i = 0; i < take; i++) {
    // A misbehaving rng (returning 1, or NaN) must not index past the end.
    // NaN is treated as 0 here: Math.max(NaN, i) is NaN, which would swap
    // `undefined` into the draw (the backend copy has that edge case).
    const r = rng();
    const j = i + Math.floor((Number.isFinite(r) ? r : 0) * (pool.length - i));
    const k = Math.min(Math.max(j, i), pool.length - 1);
    [pool[i], pool[k]] = [pool[k], pool[i]];
  }
  return pool.slice(0, take);
}
